import { forwardRef } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/cn";

type Variant = "primary" | "secondary" | "ghost" | "outline" | "danger" | "link";
type Size = "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
}

const base =
  "inline-flex max-w-full items-center justify-center gap-2 font-medium transition-all rounded-lg focus-ring disabled:opacity-50 disabled:cursor-not-allowed select-none whitespace-normal";

const variants: Record<Variant, string> = {
  primary: "bg-brand text-brand-foreground shadow-brand hover:bg-brand-hover active:scale-[0.98]",
  secondary: "bg-accent text-accent-foreground hover:bg-accent/90 active:scale-[0.98]",
  outline: "border border-line-strong bg-surface text-ink hover:bg-bg-subtle active:scale-[0.98]",
  ghost: "text-ink hover:bg-bg-subtle",
  danger: "bg-danger text-danger-foreground hover:bg-danger/90 active:scale-[0.98]",
  link: "text-brand underline-offset-4 hover:underline",
};

const sizes: Record<Size, string> = {
  sm: "min-h-9 px-3 py-1.5 text-sm",
  md: "min-h-11 px-5 py-2 text-sm",
  lg: "min-h-12 px-6 py-2 text-base",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      loading = false,
      iconLeft,
      iconRight,
      className,
      children,
      disabled,
      type = "button",
      ...rest
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        className={cn(base, variants[variant], sizes[size], className)}
        {...rest}
      >
        {loading ? (
          <span
            aria-hidden
            className="inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
          />
        ) : (
          iconLeft
        )}
        <span>{children}</span>
        {!loading && iconRight}
      </button>
    );
  },
);

Button.displayName = "Button";
